import { useContext } from 'react';
import { FF_WORKFLOW_COMMUNITY_PUBLISHING } from '@/consts/featureFlags';
import { AuthContext } from '@/contexts/AuthContext';
import { useCommunityPublisherProfiles } from '@/hooks/useCommunityPublisherProfiles';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useLinkedFigmaAccount } from '@/hooks/useLinkedFigmaAccount';
import { useFigmaOrgMembership } from '@/hooks/useOrgPublishDestinations';
import { useUserWorkflowRole, type WorkflowRole } from '@/state/workflow.state';
import {
  getCommunityPublishEligibility,
  isPaidWeaveSubscription,
  type CommunityPublishIneligibilityReason,
} from '@/utils/communityPublishingEligibility.utils';

interface CommunityPublishEligibilityState {
  canShowEntrypoint: boolean;
  canShowUpgradeUpsell: boolean;
  canPublish: boolean;
  isLoading: boolean;
  reason: CommunityPublishIneligibilityReason | null;
}

/**
 * The org-member block only applies to publishing a tool; workflows keep their Community rules.
 * @param roleOverride the caller's role where the workflow store has none (the `/tool` page).
 */
export const useCommunityPublishEligibility = (
  isToolPublish = false,
  roleOverride?: WorkflowRole,
): CommunityPublishEligibilityState => {
  const { value: isPublishToCommunityEnabled } = useFeatureFlag(FF_WORKFLOW_COMMUNITY_PUBLISHING);
  const workflowRole = useUserWorkflowRole();
  const role = roleOverride ?? workflowRole;
  const { currentUser } = useContext(AuthContext);
  const { data: figmaConnection, isLoading: isLoadingFigmaConnection } = useLinkedFigmaAccount();
  const figmaUserId = figmaConnection?.connected ? figmaConnection.figmaUserId : null;
  const { data: publisherProfiles, isLoading: isLoadingPublisherProfiles } = useCommunityPublisherProfiles(
    figmaUserId,
    isPublishToCommunityEnabled === true,
  );
  const orgMembership = useFigmaOrgMembership(isToolPublish);
  const subscriptionType = currentUser?.activeWorkspace?.subscription?.type;
  const isFeatureFlagLoading = isPublishToCommunityEnabled === undefined;
  const isResolvingEntrypoint = isFeatureFlagLoading || isLoadingFigmaConnection;
  const isLoading =
    isResolvingEntrypoint || Boolean(figmaUserId && (isLoadingPublisherProfiles || orgMembership.isLoading));

  const eligibility = getCommunityPublishEligibility({
    isFeatureEnabled: isPublishToCommunityEnabled === true,
    workflowRole: role,
    subscriptionType,
    isBannedFromCommunityPublishing: publisherProfiles?.is_banned_from_community_publishing,
    isPaidFigmaUser: publisherProfiles?.is_paid_figma_user,
    isBlockedByFigmaOrg: orgMembership.isBlockedByFigmaOrg,
  });

  // Free Weave users may still qualify through a paid Figma account, so keep the entrypoint
  // visible until they link Figma and we can fetch their publisher eligibility.
  const canLinkFigmaToQualify =
    !figmaUserId && !isPaidWeaveSubscription(subscriptionType) && eligibility.reason === 'unpaid';
  const canRetryFigmaEligibility =
    Boolean(figmaUserId) &&
    !publisherProfiles &&
    !isPaidWeaveSubscription(subscriptionType) &&
    eligibility.reason === 'unpaid';
  const canShowUpgradeUpsell =
    Boolean(figmaUserId) &&
    Boolean(publisherProfiles) &&
    publisherProfiles?.is_paid_figma_user === false &&
    eligibility.reason === 'unpaid';

  return {
    canShowEntrypoint:
      !isResolvingEntrypoint &&
      (eligibility.canPublish || canLinkFigmaToQualify || canRetryFigmaEligibility || canShowUpgradeUpsell),
    canShowUpgradeUpsell: !isLoading && canShowUpgradeUpsell,
    canPublish: !isLoading && eligibility.canPublish,
    isLoading,
    reason: isLoading ? null : eligibility.reason,
  };
};
